import firebase, { firestore } from "../firebase";
import { deleteMedia, uploadMedia } from "../media/cloudflareMedia";
import DB_PATH from "../DB_PATH";
import {
  AdPlatform,
} from "../../types/CommunityMediaConfig";
import {
  AdCopyVariant,
  AdAngleCategory,
  AdPlacement,
} from "./adsGenerator";

/**
 * A saved ad variant — the copy + its rendered image — persisted under
 * Community/{id}/AdVariants/{id}. One doc per shippable ad row.
 */
export type SavedAdVariant = {
  id: string;
  communityId: string;
  campaign: string;
  platform: AdPlatform;
  angle: AdAngleCategory;
  primaryText: string;
  primaryTextLen: number;
  primaryTextOverLimit: boolean;
  headline?: string;
  headlineLen?: number;
  headlineOverLimit?: boolean;
  description?: string;
  descriptionLen?: number;
  descriptionOverLimit?: boolean;
  cta: string;
  /** Format whose visual direction shaped this ad's image, if any. */
  formatKey?: string;
  /** Placement this image was rendered for. */
  placement?: AdPlacement;
  /** Firebase Storage download URL for the saved image. */
  imageURL?: string;
  createdAt: number;
  updatedAt: number;
};

const collection = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.AD_VARIANTS);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === "object" && !Array.isArray(v);

const sanitizeSavedAdVariant = (
  raw: unknown,
  fallbackId: string
): SavedAdVariant | null => {
  if (!isRecord(raw)) return null;
  const platform = raw.platform === "tiktok" ? "tiktok" : raw.platform === "meta" ? "meta" : null;
  if (!platform) return null;
  const primaryText = typeof raw.primaryText === "string" ? raw.primaryText : "";
  if (!primaryText) return null;
  const angle = typeof raw.angle === "string" ? (raw.angle as AdAngleCategory) : "pain-point";

  const out: SavedAdVariant = {
    id: typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : fallbackId,
    communityId: typeof raw.communityId === "string" ? raw.communityId : "",
    campaign: typeof raw.campaign === "string" ? raw.campaign : "",
    platform,
    angle,
    primaryText,
    primaryTextLen:
      typeof raw.primaryTextLen === "number" ? raw.primaryTextLen : primaryText.length,
    primaryTextOverLimit: Boolean(raw.primaryTextOverLimit),
    cta: typeof raw.cta === "string" && raw.cta.trim() ? raw.cta.trim() : "Learn More",
    createdAt:
      typeof raw.createdAt === "number"
        ? raw.createdAt
        : Date.now(),
    updatedAt:
      typeof raw.updatedAt === "number"
        ? raw.updatedAt
        : Date.now(),
  };
  if (typeof raw.headline === "string" && raw.headline.trim()) {
    out.headline = raw.headline.trim();
    out.headlineLen =
      typeof raw.headlineLen === "number" ? raw.headlineLen : out.headline.length;
    out.headlineOverLimit = Boolean(raw.headlineOverLimit);
  }
  if (typeof raw.description === "string" && raw.description.trim()) {
    out.description = raw.description.trim();
    out.descriptionLen =
      typeof raw.descriptionLen === "number"
        ? raw.descriptionLen
        : out.description.length;
    out.descriptionOverLimit = Boolean(raw.descriptionOverLimit);
  }
  if (typeof raw.formatKey === "string" && raw.formatKey.trim()) {
    out.formatKey = raw.formatKey.trim();
  }
  if (typeof raw.placement === "string" && raw.placement.trim()) {
    out.placement = raw.placement.trim() as AdPlacement;
  }
  if (typeof raw.imageURL === "string" && raw.imageURL.trim()) {
    out.imageURL = raw.imageURL.trim();
  }
  return out;
};

// ─── Image upload (data URL → provider compatibility boundary) ───────────

const uploadDataUrlToStorage = async (
  dataUrl: string,
  storagePath: string
): Promise<string> => {
  const blob = await (await fetch(dataUrl)).blob();
  return (
    await uploadMedia({
      body: blob,
      kind: "image",
      firebasePath: storagePath,
    })
  ).url;
};

// ─── Public API ───────────────────────────────────────────────────────────

export type SaveAdVariantInput = {
  communityId: string;
  campaign: string;
  variant: AdCopyVariant;
  formatKey?: string;
  placement?: AdPlacement;
  /** Optional generated image as a data URL — uploaded to Storage if provided. */
  imageDataUrl?: string;
};

export const saveAdVariant = async (
  input: SaveAdVariantInput
): Promise<SavedAdVariant> => {
  const communityId = (input.communityId || "").trim();
  if (!communityId) throw new Error("communityId is required.");
  if (!input.variant?.primaryText) throw new Error("variant is required.");

  const ref = collection(communityId).doc();
  const id = ref.id;
  const now = Date.now();

  let imageURL: string | undefined;
  if (input.imageDataUrl) {
    const ext = input.imageDataUrl.startsWith("data:image/png") ? "png" : "jpg";
    const storagePath = `Community/${communityId}/AdVariants/${id}.${ext}`;
    imageURL = await uploadDataUrlToStorage(input.imageDataUrl, storagePath);
  }

  const doc: SavedAdVariant = {
    id,
    communityId,
    campaign: input.campaign,
    platform: input.variant.platform,
    angle: input.variant.angle,
    primaryText: input.variant.primaryText,
    primaryTextLen: input.variant.primaryTextLen,
    primaryTextOverLimit: input.variant.primaryTextOverLimit,
    cta: input.variant.cta,
    createdAt: now,
    updatedAt: now,
    ...(input.variant.headline
      ? {
          headline: input.variant.headline,
          headlineLen: input.variant.headlineLen,
          headlineOverLimit: input.variant.headlineOverLimit,
        }
      : {}),
    ...(input.variant.description
      ? {
          description: input.variant.description,
          descriptionLen: input.variant.descriptionLen,
          descriptionOverLimit: input.variant.descriptionOverLimit,
        }
      : {}),
    ...(input.formatKey ? { formatKey: input.formatKey } : {}),
    ...(input.placement ? { placement: input.placement } : {}),
    ...(imageURL ? { imageURL } : {}),
  };

  await ref.set(doc);
  return doc;
};

export const listSavedAdVariants = async (
  communityId: string
): Promise<SavedAdVariant[]> => {
  if (!communityId) return [];
  const snap = await collection(communityId).orderBy("createdAt", "desc").get();
  const out: SavedAdVariant[] = [];
  snap.docs.forEach((doc) => {
    const sanitized = sanitizeSavedAdVariant(doc.data(), doc.id);
    if (sanitized) out.push(sanitized);
  });
  return out;
};

export const deleteSavedAdVariant = async (
  communityId: string,
  variantId: string
): Promise<void> => {
  if (!communityId || !variantId) return;
  const ref = collection(communityId).doc(variantId);
  const snap = await ref.get();
  const data = snap.data();
  // Best-effort image cleanup — ignore failures (storage rules may differ).
  if (data && typeof data.imageURL === "string" && data.imageURL.trim()) {
    try {
      await deleteMedia({ url: data.imageURL });
    } catch (e) {
      console.warn("Failed to delete ad variant image from Storage:", e);
    }
  }
  await ref.delete();
};
